import { useState } from 'react';
import { motion } from 'framer-motion';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import axios from 'axios';
import { 
  Mail, Phone, MapPin, Clock, MessageCircle, 
  CheckCircle, Loader2, AlertCircle, ArrowRight
} from 'lucide-react';

import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// GTM tracking helper
const trackEvent = (eventName, eventData = {}) => {
  if (window.dataLayer) {
    window.dataLayer.push({
      event: eventName,
      ...eventData
    });
  }
};

const contactSchema = z.object({
  name: z.string().min(2, 'Name is required'),
  email: z.string().email('Valid email is required'),
  phone: z.string().min(10, 'Contact details are required'),
  company: z.string().min(1, 'Company / Brand is required'),
  has_ecommerce_store: z.string().min(1, 'Please select an option'),
  interest: z.string().min(1, 'Please select your interest'),
  parcel_sizes: z.array(z.string()).min(1, 'Please select at least one parcel size'),
  custom_branding: z.string().min(1, 'Please select an option'),
  max_weight_ok: z.string().min(1, 'Please select an option'),
  store_url: z.string().min(1, 'Please provide your online store URL'),
});

const ecommerceStoreOptions = [
  { value: 'yes', label: 'Yes' },
  { value: 'no', label: 'No' },
];

const interestOptions = [
  { value: 'delivery', label: 'Deliveries' },
];

const parcelSizeOptions = [
  { value: 'stock_box_1', label: 'Stock Box 1' },
  { value: 'stock_box_2', label: 'Stock Box 2' },
  { value: 'stock_box_3', label: 'Stock Box 3' },
  { value: 'stock_box_4_plus', label: 'Stock Box 4 and up' },
];

const customBrandingOptions = [
  { value: 'yes', label: 'Yes, all good' },
  { value: 'no_open', label: 'No, would you be open to a customer experience focused delivery service?' },
];

export function ContactPage() {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);
  const [selectedParcelSizes, setSelectedParcelSizes] = useState([]);
  const [hasEcommerceStore, setHasEcommerceStore] = useState('');
  const [hasOversizedParcels, setHasOversizedParcels] = useState(false);
  const [isOverweight, setIsOverweight] = useState(false);

  // Check if form can be submitted
  const cannotSubmitNoStore = hasEcommerceStore === 'no';
  const cannotSubmitOversized = hasOversizedParcels;
  const cannotSubmitOverweight = isOverweight;
  const formBlocked = cannotSubmitNoStore || cannotSubmitOversized || cannotSubmitOverweight;

  const {
    register,
    handleSubmit,
    setValue,
    reset,
    formState: { errors },
  } = useForm({
    resolver: zodResolver(contactSchema),
    defaultValues: {
      interest: '',
      has_ecommerce_store: '',
      parcel_sizes: [],
      custom_branding: '',
      max_weight_ok: '',
    },
  });

  const handleEcommerceStoreChange = (value) => {
    setHasEcommerceStore(value);
    setValue('has_ecommerce_store', value);
  };

  const handleParcelSizeChange = (value, checked) => {
    const newSizes = checked 
      ? [...selectedParcelSizes, value]
      : selectedParcelSizes.filter(s => s !== value);
    setSelectedParcelSizes(newSizes);
    setValue('parcel_sizes', newSizes);
    
    // Check if "Stock Box 4 and up" is selected
    setHasOversizedParcels(newSizes.includes('stock_box_4_plus'));
  };

  const onSubmit = async (data) => {
    setIsSubmitting(true);
    setSubmitStatus(null);

    trackEvent('contact_form_submit', {
      interest: data.interest,
      has_ecommerce_store: data.has_ecommerce_store,
      custom_branding: data.custom_branding
    });

    try {
      await axios.post(`${API}/contact`, data);
      setSubmitStatus('success');
      reset();
      setSelectedParcelSizes([]);
      setHasEcommerceStore('');
      trackEvent('contact_form_success');
    } catch (error) {
      console.error('Form submission error:', error);
      setSubmitStatus('error');
      trackEvent('contact_form_error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEmailClick = () => {
    trackEvent('contact_click', { contact_type: 'email', contact_value: 'online@drop24.co.za' });
  };

  const handlePhoneClick = () => {
    trackEvent('contact_click', { contact_type: 'phone', contact_value: '+27660719068' });
  };

  const handleWhatsAppClick = () => {
    trackEvent('contact_click', { contact_type: 'whatsapp', contact_value: '+27660719068' });
  };

  return (
    <div data-testid="contact-page">
      {/* Hero */}
      <section className="pt-32 pb-16 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <span className="text-drop24-red font-semibold text-sm uppercase tracking-wider">Contact Us</span>
            <h1 
              data-testid="contact-hero-title"
              className="font-serif text-5xl md:text-6xl text-gray-900 mt-4 mb-6"
            >
              Let's <span className="text-gradient">Connect</span>
            </h1>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Ready to transform your delivery experience? Get in touch and let's discuss 
              how Drop24 can help your brand shine.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid lg:grid-cols-3 gap-12">
            {/* Contact Info */}
            <div className="lg:col-span-1">
              <h2 className="font-serif text-3xl text-gray-900 mb-8">Contact Info</h2>
              
              <div className="space-y-4">
                <motion.a
                  href="mailto:deliveries@drop24.co.za"
                  onClick={() => trackEvent('contact_click', { contact_type: 'email', contact_value: 'deliveries@drop24.co.za' })}
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  className="flex items-center gap-4 p-4 bg-drop24-red-light rounded-2xl hover:shadow-soft transition-all group"
                  data-testid="contact-deliveries-email-link"
                >
                  <div className="w-12 h-12 bg-white rounded-xl flex items-center justify-center group-hover:scale-105 transition-transform">
                    <Mail className="w-5 h-5 text-drop24-red" />
                  </div>
                  <div>
                    <p className="font-semibold text-gray-900">Track Your Delivery</p>
                    <p className="text-gray-600">deliveries@drop24.co.za</p>
                  </div>
                </motion.a>

                <motion.a
                  href="tel:+27660719068"
                  onClick={handlePhoneClick}
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: 0.2 }}
                  className="flex items-center gap-4 p-4 bg-gray-50 rounded-2xl hover:shadow-soft transition-all group"
                  data-testid="contact-phone-link"
                >
                  <div className="w-12 h-12 bg-white rounded-xl flex items-center justify-center group-hover:scale-105 transition-transform">
                    <Phone className="w-5 h-5 text-drop24-red" />
                  </div>
                  <div>
                    <p className="font-semibold text-gray-900">Phone</p>
                    <p className="text-gray-600">066 071 9068</p>
                  </div>
                </motion.a>

                <motion.a
                  href="https://wa.me/27660719068"
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => trackEvent('contact_click', { contact_type: 'whatsapp', contact_value: '+27660719068' })}
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: 0.3 }}
                  className="flex items-center gap-4 p-4 bg-green-50 rounded-2xl hover:shadow-soft transition-all group"
                  data-testid="contact-whatsapp-link-1"
                >
                  <div className="w-12 h-12 bg-white rounded-xl flex items-center justify-center group-hover:scale-105 transition-transform">
                    <MessageCircle className="w-5 h-5 text-green-600" />
                  </div>
                  <div>
                    <p className="font-semibold text-gray-900">WhatsApp</p>
                    <p className="text-gray-600">066 071 9068</p>
                  </div>
                </motion.a>

                <motion.div
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: 0.4 }}
                  className="flex items-center gap-4 p-4 bg-gray-50 rounded-2xl"
                >
                  <div className="w-12 h-12 bg-white rounded-xl flex items-center justify-center">
                    <MapPin className="w-5 h-5 text-drop24-red" />
                  </div>
                  <div>
                    <p className="font-semibold text-gray-900">Location</p>
                    <p className="text-gray-600">Gauteng, South Africa</p>
                  </div>
                </motion.div>

                <motion.div
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: 0.5 }}
                  className="flex items-center gap-4 p-4 bg-gray-50 rounded-2xl"
                >
                  <div className="w-12 h-12 bg-white rounded-xl flex items-center justify-center">
                    <Clock className="w-5 h-5 text-drop24-red" />
                  </div>
                  <div>
                    <p className="font-semibold text-gray-900">Business Hours</p>
                    <p className="text-gray-600">Mon-Fri: 8am-6pm</p>
                    <p className="text-gray-600">Sat: 8am-1pm</p>
                  </div>
                </motion.div>
              </div>
            </div>

            {/* Contact Form */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="lg:col-span-2 bg-white rounded-3xl p-8 shadow-soft border border-gray-100"
            >
              <h3 className="font-serif text-2xl text-gray-900 mb-2">Send Us a Message</h3>
              <p className="text-gray-600 mb-8">
                Tell us about your business and logistics needs.
              </p>

              <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
                {/* 1. Name and 2. Email */}
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <Label htmlFor="name" className="text-sm font-semibold text-gray-700 mb-2 block">
                      1. Name *
                    </Label>
                    <Input
                      id="name"
                      data-testid="contact-name-input"
                      placeholder="Your name"
                      className="h-12 rounded-xl border-gray-200 focus:ring-2 focus:ring-drop24-red/20 focus:border-drop24-red"
                      {...register('name')}
                    />
                    {errors.name && (
                      <p className="text-red-600 text-sm mt-1">{errors.name.message}</p>
                    )}
                  </div>

                  <div>
                    <Label htmlFor="email" className="text-sm font-semibold text-gray-700 mb-2 block">
                      2. Email *
                    </Label>
                    <Input
                      id="email"
                      type="email"
                      data-testid="contact-email-input"
                      placeholder="your@email.com"
                      className="h-12 rounded-xl border-gray-200 focus:ring-2 focus:ring-drop24-red/20 focus:border-drop24-red"
                      {...register('email')}
                    />
                    {errors.email && (
                      <p className="text-red-600 text-sm mt-1">{errors.email.message}</p>
                    )}
                  </div>
                </div>

                {/* 3. Contact Details and 4. Company/Brand */}
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <Label htmlFor="phone" className="text-sm font-semibold text-gray-700 mb-2 block">
                      3. Contact Details *
                    </Label>
                    <Input
                      id="phone"
                      data-testid="contact-phone-input"
                      placeholder="+27..."
                      className="h-12 rounded-xl border-gray-200 focus:ring-2 focus:ring-drop24-red/20 focus:border-drop24-red"
                      {...register('phone')}
                    />
                    {errors.phone && (
                      <p className="text-red-600 text-sm mt-1">{errors.phone.message}</p>
                    )}
                  </div>

                  <div>
                    <Label htmlFor="company" className="text-sm font-semibold text-gray-700 mb-2 block">
                      4. Company / Brand *
                    </Label>
                    <Input
                      id="company"
                      data-testid="contact-company-input"
                      placeholder="Your business name"
                      className="h-12 rounded-xl border-gray-200 focus:ring-2 focus:ring-drop24-red/20 focus:border-drop24-red"
                      {...register('company')}
                    />
                    {errors.company && (
                      <p className="text-red-600 text-sm mt-1">{errors.company.message}</p>
                    )}
                  </div>
                </div>

                {/* 5. Ecommerce Store */}
                <div>
                  <Label className="text-sm font-semibold text-gray-700 mb-2 block">
                    5. Do you have an ecommerce store? *
                  </Label>
                  <Select onValueChange={handleEcommerceStoreChange}>
                    <SelectTrigger 
                      data-testid="contact-ecommerce-select"
                      className={`h-12 rounded-xl ${cannotSubmitNoStore ? 'border-red-300 bg-red-50' : 'border-gray-200'}`}
                    >
                      <SelectValue placeholder="Select an option" />
                    </SelectTrigger>
                    <SelectContent>
                      {ecommerceStoreOptions.map((option) => (
                        <SelectItem key={option.value} value={option.value}>
                          {option.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {errors.has_ecommerce_store && (
                    <p className="text-red-600 text-sm mt-1">{errors.has_ecommerce_store.message}</p>
                  )}
                  {cannotSubmitNoStore && (
                    <motion.div
                      initial={{ opacity: 0, y: -5 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="mt-3 p-3 bg-red-50 border border-red-200 rounded-xl"
                    >
                      <p className="text-red-700 text-sm font-medium flex items-center gap-2">
                        <AlertCircle className="w-4 h-4" />
                        Drop24 services are only available for ecommerce stores.
                      </p>
                      <p className="text-red-600 text-xs mt-1">
                        Please contact us when you have an online store ready.
                      </p>
                    </motion.div>
                  )}
                </div>

                {/* 6. What are you interested in? */}
                <div>
                  <Label className="text-sm font-semibold text-gray-700 mb-2 block">
                    6. What are you interested in? *
                  </Label>
                  <Select onValueChange={(value) => setValue('interest', value)}>
                    <SelectTrigger 
                      data-testid="contact-interest-select"
                      className="h-12 rounded-xl border-gray-200"
                    >
                      <SelectValue placeholder="Select an option" />
                    </SelectTrigger>
                    <SelectContent>
                      {interestOptions.map((option) => (
                        <SelectItem key={option.value} value={option.value}>
                          {option.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {errors.interest && (
                    <p className="text-red-600 text-sm mt-1">{errors.interest.message}</p>
                  )}
                </div>

                {/* 7. Parcel Sizes */}
                <div>
                  <Label className="text-sm font-semibold text-gray-700 mb-3 block">
                    7. What size parcels do you send? (Select all that apply) *
                  </Label>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                    {parcelSizeOptions.map((option) => (
                      <div 
                        key={option.value} 
                        className={`flex items-center space-x-2 p-3 rounded-xl transition-colors ${
                          option.value === 'stock_box_4_plus' && hasOversizedParcels
                            ? 'bg-red-50 border border-red-200'
                            : 'bg-gray-50 hover:bg-gray-100'
                        }`}
                      >
                        <Checkbox
                          id={option.value}
                          data-testid={`parcel-size-${option.value}`}
                          checked={selectedParcelSizes.includes(option.value)}
                          onCheckedChange={(checked) => handleParcelSizeChange(option.value, checked)}
                          className="border-gray-300 data-[state=checked]:bg-drop24-red data-[state=checked]:border-drop24-red"
                        />
                        <label
                          htmlFor={option.value}
                          className="text-sm text-gray-700 cursor-pointer"
                        >
                          {option.label}
                        </label>
                      </div>
                    ))}
                  </div>
                  {errors.parcel_sizes && (
                    <p className="text-red-600 text-sm mt-1">{errors.parcel_sizes.message}</p>
                  )}
                  {hasOversizedParcels && (
                    <motion.div
                      initial={{ opacity: 0, y: -5 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="mt-3 p-3 bg-red-50 border border-red-200 rounded-xl"
                    >
                      <p className="text-red-700 text-sm font-medium flex items-center gap-2">
                        <AlertCircle className="w-4 h-4" />
                        Parcels larger than Stock Box 3 are outside our service capabilities.
                      </p>
                      <p className="text-red-600 text-xs mt-1">
                        Drop24 specializes in small, high-value parcels up to Stock Box 3.
                      </p>
                    </motion.div>
                  )}
                </div>

                {/* 8. Custom Branding */}
                <div>
                  <Label className="text-sm font-semibold text-gray-700 mb-2 block">
                    8. Do you prioritise customer experience in your Deliveries? *
                  </Label>
                  <Select onValueChange={(value) => setValue('custom_branding', value)}>
                    <SelectTrigger 
                      data-testid="contact-branding-select"
                      className="h-12 rounded-xl border-gray-200"
                    >
                      <SelectValue placeholder="Select an option" />
                    </SelectTrigger>
                    <SelectContent>
                      {customBrandingOptions.map((option) => (
                        <SelectItem key={option.value} value={option.value}>
                          {option.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {errors.custom_branding && (
                    <p className="text-red-600 text-sm mt-1">{errors.custom_branding.message}</p>
                  )}
                </div>

                {/* 9. Maximum Weight */}
                <div>
                  <Label className="text-sm font-semibold text-gray-700 mb-2 block">
                    9. Maximum Weight 3 kilograms? *
                  </Label>
                  <Select onValueChange={(value) => {
                    setValue('max_weight_ok', value);
                    setIsOverweight(value === 'no');
                  }}>
                    <SelectTrigger 
                      data-testid="contact-weight-select"
                      className="h-12 rounded-xl border-gray-200"
                    >
                      <SelectValue placeholder="Select an option" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="yes">Yes, under 3kg</SelectItem>
                      <SelectItem value="no">No, over 3kg</SelectItem>
                    </SelectContent>
                  </Select>
                  {errors.max_weight_ok && (
                    <p className="text-red-600 text-sm mt-1">{errors.max_weight_ok.message}</p>
                  )}
                  {isOverweight && (
                    <motion.div
                      initial={{ opacity: 0, y: -5 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="mt-2 p-2 bg-red-50 border border-red-200 rounded-lg"
                    >
                      <p className="text-red-700 text-xs font-medium flex items-center gap-1">
                        <AlertCircle className="w-3 h-3" />
                        Drop24 can only deliver orders under 3 kilograms.
                      </p>
                    </motion.div>
                  )}
                </div>

                {/* 10. Online Store URL */}
                <div>
                  <Label htmlFor="store-url" className="text-sm font-semibold text-gray-700 mb-2 block">
                    10. Please provide us with your online store URL?
                  </Label>
                  <Input
                    id="store-url"
                    data-testid="contact-store-url-input"
                    placeholder="https://yourstore.co.za"
                    className="h-12 rounded-xl border-gray-200 focus:ring-2 focus:ring-drop24-red/20 focus:border-drop24-red"
                    {...register('store_url')}
                  />
                  {errors.store_url && (
                    <p className="text-red-600 text-sm mt-1">{errors.store_url.message}</p>
                  )}
                </div>
                <button
                  type="submit"
                  disabled={isSubmitting || formBlocked}
                  data-testid="contact-submit-button"
                  className={`w-full px-8 py-4 rounded-full font-semibold transition-all flex items-center justify-center gap-2 ${
                    formBlocked 
                      ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                      : 'bg-drop24-red text-white hover:bg-[#c4191f] shadow-soft hover:shadow-soft-lg disabled:opacity-50 disabled:cursor-not-allowed'
                  }`}
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 className="w-5 h-5 animate-spin" />
                      Sending...
                    </>
                  ) : formBlocked ? (
                    <>
                      <AlertCircle className="w-5 h-5" />
                      Cannot Submit
                    </>
                  ) : (
                    <>
                      Send Message
                      <ArrowRight className="w-5 h-5" />
                    </>
                  )}
                </button>

                {submitStatus === 'success' && (
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    data-testid="contact-success-message"
                    className="flex items-center gap-3 p-4 bg-green-50 border border-green-100 text-green-700 rounded-2xl"
                  >
                    <CheckCircle className="w-5 h-5 flex-shrink-0" />
                    <p>Thank you! Your message has been sent. We'll be in touch shortly.</p>
                  </motion.div>
                )}

                {submitStatus === 'error' && (
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    data-testid="contact-error-message"
                    className="flex items-center gap-3 p-4 bg-red-50 border border-red-100 text-red-700 rounded-2xl"
                  >
                    <AlertCircle className="w-5 h-5 flex-shrink-0" />
                    <p>Something went wrong. Please try again or contact us directly.</p>
                  </motion.div>
                )}
              </form>
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  );
}

export default ContactPage;
